import React, { useEffect, useState } from 'react';
import { Link } from 'react-router-dom';
import axios from 'axios';
import { motion } from 'framer-motion';
import { Storefront, Package, Image as ImageIcon, PlayCircle, Coins, FunnelSimple, MagnifyingGlass } from '@phosphor-icons/react';

const API = process.env.REACT_APP_BACKEND_URL;

const KIND_ICONS = {
  bundle: Package,
  photo: ImageIcon,
  video: PlayCircle,
};

const GlobalStorePage = () => {
  const [rows, setRows] = useState([]);
  const [categories, setCategories] = useState([]);
  const [loading, setLoading] = useState(true);

  // filter state
  const [filterType, setFilterType] = useState('');
  const [filterCategory, setFilterCategory] = useState('');
  const [filterSort, setFilterSort] = useState('newest');
  const [maxPrice, setMaxPrice] = useState('');
  const [search, setSearch] = useState('');
  const [filteredItems, setFilteredItems] = useState(null);

  useEffect(() => {
    (async () => {
      try {
        const [curated, cats] = await Promise.all([
          axios.get(`${API}/api/global-store/curated`),
          axios.get(`${API}/api/global-store/categories`),
        ]);
        setRows(curated.data.rows);
        setCategories(cats.data);
      } catch {} finally { setLoading(false); }
    })();
  }, []);

  const applyFilters = async () => {
    setLoading(true);
    try {
      const params = { sort: filterSort, limit: 60 };
      if (filterType) params.type = filterType;
      if (filterCategory) params.category = filterCategory;
      if (maxPrice) params.max_price = parseInt(maxPrice);
      const res = await axios.get(`${API}/api/global-store/items`, { params });
      let items = res.data;
      if (search.trim()) {
        const q = search.toLowerCase();
        items = items.filter(i =>
          (i.title || '').toLowerCase().includes(q) ||
          (i.creator_name || '').toLowerCase().includes(q)
        );
      }
      setFilteredItems(items);
    } finally { setLoading(false); }
  };

  const clearFilters = () => {
    setFilterType(''); setFilterCategory(''); setFilterSort('newest');
    setMaxPrice(''); setSearch(''); setFilteredItems(null);
  };

  return (
    <div className="max-w-7xl mx-auto px-4 py-6" data-testid="global-store-page">
      {/* Header */}
      <div className="mb-6">
        <h1 className="font-heading text-3xl md:text-4xl font-bold text-white flex items-center gap-3">
          <Storefront weight="fill" className="w-9 h-9 text-blue-400" />
          The Vault
        </h1>
        <p className="text-slate-400 text-sm mt-1">Discover content across every creator — bundles, photos, and videos.</p>
      </div>

      {/* Filter bar */}
      <div className="glass-light rounded-2xl p-4 mb-6">
        <div className="flex items-center gap-2 flex-wrap">
          <div className="flex-1 min-w-[200px] relative">
            <MagnifyingGlass className="w-4 h-4 text-slate-500 absolute left-3 top-1/2 -translate-y-1/2" />
            <input
              type="text" value={search} onChange={e => setSearch(e.target.value)}
              onKeyDown={e => e.key === 'Enter' && applyFilters()}
              placeholder="Search items or creators…"
              className="w-full bg-slate-800 border border-white/10 rounded-lg pl-9 pr-3 py-2 text-sm text-white placeholder:text-slate-600 focus:outline-none focus:border-blue-500/50"
              data-testid="store-search"
            />
          </div>
          <select value={filterType} onChange={e => setFilterType(e.target.value)}
            className="bg-slate-800 border border-white/10 rounded-lg px-3 py-2 text-sm text-white">
            <option value="">All types</option>
            <option value="bundle">Bundles</option>
            <option value="photo">Photos</option>
            <option value="video">Videos</option>
          </select>
          <select value={filterCategory} onChange={e => setFilterCategory(e.target.value)}
            className="bg-slate-800 border border-white/10 rounded-lg px-3 py-2 text-sm text-white capitalize">
            <option value="">All categories</option>
            {categories.map(c => <option key={c} value={c}>{c}</option>)}
          </select>
          <input type="number" placeholder="Max tokens" value={maxPrice}
            onChange={e => setMaxPrice(e.target.value)}
            className="w-28 bg-slate-800 border border-white/10 rounded-lg px-3 py-2 text-sm text-white placeholder:text-slate-600" />
          <select value={filterSort} onChange={e => setFilterSort(e.target.value)}
            className="bg-slate-800 border border-white/10 rounded-lg px-3 py-2 text-sm text-white">
            <option value="newest">Newest</option>
            <option value="bestselling">Best-selling</option>
          </select>
          <button onClick={applyFilters}
            className="px-4 py-2 bg-blue-500 text-white rounded-lg text-sm font-semibold hover:bg-blue-600 flex items-center gap-1"
            data-testid="apply-filters-btn">
            <FunnelSimple className="w-4 h-4" /> Apply
          </button>
          {filteredItems && (
            <button onClick={clearFilters} className="text-xs text-slate-400 hover:text-white px-2"
              data-testid="clear-filters-btn">Clear</button>
          )}
        </div>
      </div>

      {loading && <p className="text-slate-500 text-sm">Loading…</p>}

      {/* Filtered results */}
      {filteredItems && !loading && (
        <div>
          <p className="text-slate-500 text-sm mb-3">{filteredItems.length} result{filteredItems.length !== 1 ? 's' : ''}</p>
          {filteredItems.length === 0 ? (
            <div className="text-center py-12 glass-light rounded-2xl">
              <p className="text-slate-500">No items match your filters.</p>
            </div>
          ) : (
            <ItemGrid items={filteredItems} />
          )}
        </div>
      )}

      {/* Curated rows */}
      {!filteredItems && !loading && rows.map(r => (
        <section key={r.key} className="mb-10" data-testid={`row-${r.key}`}>
          <h2 className="font-heading text-xl font-bold text-white mb-3">{r.title}</h2>
          {r.items.length === 0 ? (
            <p className="text-slate-600 text-sm">Nothing here yet — check back soon.</p>
          ) : (
            <ItemGrid items={r.items} />
          )}
        </section>
      ))}
    </div>
  );
};

const ItemGrid = ({ items }) => (
  <div className="grid grid-cols-2 sm:grid-cols-3 md:grid-cols-4 lg:grid-cols-5 gap-3">
    {items.map((it, idx) => {
      const Icon = KIND_ICONS[it.kind] || Package;
      return (
        <motion.div key={`${it.kind}-${it.id}`}
          initial={{ opacity: 0, y: 8 }} animate={{ opacity: 1, y: 0 }} transition={{ delay: idx * 0.02 }}
          className="group" data-testid={`store-item-${it.id}`}>
          <Link to={`/profile/${it.creator_id}`} className="block">
            <div className="aspect-[3/4] rounded-xl overflow-hidden bg-slate-900 relative">
              {it.preview_url ? (
                <img src={it.preview_url} alt={it.title}
                  className="w-full h-full object-cover transition-transform group-hover:scale-105" />
              ) : (
                <div className="w-full h-full flex items-center justify-center">
                  <Icon className="w-10 h-10 text-slate-700" />
                </div>
              )}
              <span className="absolute top-2 left-2 px-2 py-0.5 bg-black/70 backdrop-blur-md text-white text-[10px] uppercase tracking-wider font-bold rounded-full">
                {it.kind}
              </span>
              {it.store_category && (
                <span className="absolute top-2 right-2 px-2 py-0.5 bg-blue-500/80 backdrop-blur-md text-white text-[10px] uppercase tracking-wider font-bold rounded-full">
                  {it.store_category}
                </span>
              )}
              <div className="absolute bottom-0 left-0 right-0 p-2 bg-gradient-to-t from-black/90 to-transparent">
                <p className="text-white font-medium text-sm truncate">{it.title}</p>
                <div className="flex items-center justify-between mt-0.5">
                  <span className="text-slate-400 text-xs truncate">{it.creator_name}</span>
                  <span className="flex items-center gap-0.5 text-amber-400 font-bold text-xs">
                    <Coins weight="fill" className="w-3 h-3" />{it.price_tokens}
                  </span>
                </div>
              </div>
            </div>
          </Link>
        </motion.div>
      );
    })}
  </div>
);

export default GlobalStorePage;
